import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardNotificationStore, $profileFeatureStore } from '@lib/store';
import { useQuery, useRouteHook } from '@shared/hooks';
import { cn, DateUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import type { Notification } from '@shared/api';
import { PageContainer } from '@widgets';
import { Button, Modal, Table, TableCellType, Text } from '@shared/widgets';

export const AlertsPage = () => {
  const query = useQuery();
  const id = query.get('id');
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const [showAlertModal, setShowAlertModal] = useState<Notification | null>(null);

  const { t } = useTranslation('alerts');

  useEffect(() => {
    if (!profileFeatureStore.data?.settings?.viewNotifications) {
      navigate(RoutePaths.dashboard.onNavigate());
      return;
    }

    $dashboardNotificationStore.events.getAll();
  }, []);

  useEffect(() => {
    if (dashboardNotificationStore.loading || !dashboardNotificationStore.data.length || !id) return;
    const notification = dashboardNotificationStore.data.find((notification) => notification.alertGuid === id) || null;
    setShowAlertModal(notification);
  }, [dashboardNotificationStore.loading, dashboardNotificationStore.data, id]);

  const isUnread = useMemo(
    () => dashboardNotificationStore.data.length > 0 && dashboardNotificationStore.data.filter((notification) => !notification.isRead).length > 0,
    [dashboardNotificationStore.data],
  );
  const columns = useMemo(() => [t('table.columns.alerts'), t('table.columns.details'), t('table.columns.alertTime'), ''], [t]);

  if (!profileFeatureStore.data?.settings?.viewNotifications) return null;
  return (
    <PageContainer title={t('title')} back backText={t('backText')}>
      <div className='mb-6 flex justify-between'>
        <Text size='2xl'>{t('header.title')}</Text>
        <Button
          color='outlined'
          disabled={dashboardNotificationStore.loading || !isUnread}
          onClick={() => $dashboardNotificationStore.events.markAllAsRead({ allNotifications: true })}
        >
          {t('header.buttons.markAllRead')}
        </Button>
      </div>
      <div className={cn(PaletteUtils.isVcfcPalette && 'rounded-lg bg-white')}>
        <Table
          isScrollable
          isLoading={dashboardNotificationStore.loading}
          error={dashboardNotificationStore.error}
          columns={columns}
          items={dashboardNotificationStore.data.map((alert) => [
            {
              type: TableCellType.Custom,
              value: (
                <div className='flex items-center gap-2'>
                  {!alert.isRead && <div className='my-auto h-2 w-2 rounded-full bg-green' />}
                  <Text>{alert.alertTypeDisplayString}</Text>
                </div>
              ),
            },
            { type: TableCellType.Text, value: `${alert.subject?.slice(0, 50)}...` },
            { type: TableCellType.Text, value: DateUtils.formatDate(alert.createdOn, { withTime: true }) },
            {
              type: TableCellType.Custom,
              value: (
                <Button fullWidth color='outlined' onClick={() => setShowAlertModal(alert)}>
                  {t('table.buttons.viewDetails')}
                </Button>
              ),
            },
          ])}
          onRefresh={() => $dashboardNotificationStore.events.getAll()}
        />
      </div>
      <Modal isOpen={!!showAlertModal} onClose={() => setShowAlertModal(null)} title={t('modals.details.title')} width='332px'>
        <Text className='whitespace-pre-line'>{showAlertModal?.text}</Text>
        <br />
        {showAlertModal?.actionUrl && (
          <Button fullWidth color='primary' to={showAlertModal?.actionUrl} onClick={() => setShowAlertModal(null)}>
            {showAlertModal?.actionText}
          </Button>
        )}
      </Modal>
    </PageContainer>
  );
};
